import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { INR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Car, PackageCheck, Truck, TrendingUp, Landmark, AlertCircle, ArrowLeftRight, Calendar, Target, Pencil, BookOpen, Coins, Banknote, Clock, PackageOpen, PackageX } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, subvalue, subline, accent, testid, onClick }) => (
  <div className={`stat-card relative overflow-hidden ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
       data-testid={testid} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : -1}
       onKeyDown={(e) => { if (onClick && (e.key === "Enter" || e.key === " ")) onClick(); }}>
    <div className={`absolute top-0 left-0 w-full h-1 ${accent}`} />
    <div className="flex items-center justify-between">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
    <div className="mt-4 font-heading text-3xl font-bold text-slate-900">{value}</div>
    {subline ? (
      <div className="mt-1 text-xs font-medium text-slate-600">{subline}</div>
    ) : subvalue ? (
      <div className="mt-1 text-xs font-medium text-slate-500">
        NDP <span className="text-slate-700 font-semibold">{subvalue}</span>
      </div>
    ) : null}
  </div>
);

const SUB_DEALERS = ["Alok Automobile", "Dilip Enterprises"];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [adv, setAdv] = useState(null);
  const [targetData, setTargetData] = useState(null); // {date_from, date_to, months, summary, branches}
  const [editing, setEditing] = useState(false);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [ledgerSubDealer, setLedgerSubDealer] = useState("");

  // Default target-tracker range = first of current month → today
  const monthStart = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  };
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const [targetFrom, setTargetFrom] = useState(monthStart);
  const [targetTo, setTargetTo] = useState(todayIso);

  const isAdmin = user?.role === "admin";
  // Net Profit widget state (admin only). Filter by explicit date range OR by FY.
  const [profitMode, setProfitMode] = useState("range"); // 'range' | 'fy'
  const [profitFrom, setProfitFrom] = useState(monthStart);
  const [profitTo, setProfitTo] = useState(todayIso);
  const currentFy = () => {
    const d = new Date();
    const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  };
  const [profitFy, setProfitFy] = useState(currentFy());
  const [profit, setProfit] = useState(null);

  const loadProfit = () => {
    if (!isAdmin) return;
    const params = profitMode === "fy" ? { fy: profitFy } : { date_from: profitFrom, date_to: profitTo };
    api.get("/dashboard/net-profit", { params }).then(({ data }) => setProfit(data)).catch(() => {});
  };

  const loadTargets = (from = targetFrom, to = targetTo) => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    return api.get("/branch-targets", { params }).then(({ data }) => setTargetData(data));
  };

  const loadLedgerSummary = (sd) => {
    const params = sd ? { sub_dealer: sd } : {};
    api.get("/ledgers/summary", { params }).then(({ data }) => setLedgerSummary(data)).catch(() => {});
  };

  const showLedgerWidget = user?.role === "admin" || (user?.role === "branch" && SUB_DEALERS.includes(user?.branch));

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
    api.get("/dashboard/advanced").then(({ data }) => setAdv(data));
    loadTargets(targetFrom, targetTo);
    if (showLedgerWidget) loadLedgerSummary(ledgerSubDealer);
    loadProfit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerSubDealer, showLedgerWidget, targetFrom, targetTo, profitMode, profitFrom, profitTo, profitFy, isAdmin]);

  if (!stats) {
    return <div className="text-slate-500 text-sm">Loading dashboard…</div>;
  }

  const targets = targetData?.branches || [];

  const branchData = [...stats.branch_stock].sort((a, b) => b.count - a.count);
  const modelData = [...stats.model_stock].sort((a, b) => b.count - a.count);
  const COLORS = ["#1E3A8A", "#F97316", "#3B82F6", "#0EA5E9", "#8B5CF6"];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="dashboard-heading">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Live overview of stock, deliveries and revenue</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon={Car} label="Total Vehicles" value={stats.total_vehicles}
          subvalue={stats.can_see_ndp ? INR(stats.total_ndp) : null}
          accent="bg-blue-900" testid="stat-total-vehicles"
          onClick={() => navigate("/reports?tab=stock")} />
        <StatCard icon={PackageCheck} label="In Stock" value={stats.in_stock}
          subvalue={stats.can_see_ndp ? INR(stats.in_stock_ndp) : null}
          accent="bg-emerald-600" testid="stat-in-stock"
          onClick={() => navigate("/reports?tab=stock&status=In+Stock")} />
        <StatCard icon={Truck} label="Delivered" value={stats.delivered}
          accent="bg-orange-500" testid="stat-delivered"
          onClick={() => navigate("/reports?tab=stock&status=Delivered")} />
        <StatCard icon={AlertCircle} label="Dues Files"
          value={<span><span data-testid="dues-count">{stats.pending_finance}</span> <span className="text-base font-medium text-slate-500">Customers</span></span>}
          subline={<span className="text-sm font-semibold text-amber-700"><span data-testid="dues-amount">{INR(stats.pending_finance_amount || 0)}</span> <span className="text-slate-500 font-normal">Outstanding Balance</span></span>}
          accent="bg-amber-500" testid="stat-pending-finance"
          onClick={() => navigate("/reports?tab=finance&filter=pending")} />
      </div>

      {/* Disbursement + Delivery Order cards (Loan-only, Delivered vehicles) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
          className="stat-card relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-green-50 border-green-200"
          data-testid="stat-loan-disbursed"
          role="button" tabIndex={0}
          onClick={() => navigate("/finance?disbursement=Yes")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/finance?disbursement=Yes"); }}>
          <div className="absolute top-0 left-0 w-full h-1 bg-green-500" />
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-green-800">Loan Disbursed</div>
            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-green-500">
              <Banknote className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="mt-4 font-heading text-3xl font-bold text-green-900">
            <span data-testid="disbursed-count">{stats.disbursed_files || 0}</span>
            <span className="text-base font-medium text-green-700 ml-1">Files</span>
          </div>
          <div className="mt-1 text-sm font-semibold text-green-800">
            <span data-testid="disbursed-amount">{INR(stats.disbursed_loan_amount || 0)}</span>
            <span className="text-green-700 font-normal ml-1">Total Disbursed</span>
          </div>
        </div>

        <div
          className="stat-card relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-orange-50 border-orange-200"
          data-testid="stat-loan-disbursement-pending"
          role="button" tabIndex={0}
          onClick={() => navigate("/finance?disbursement=Pending")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/finance?disbursement=Pending"); }}>
          <div className="absolute top-0 left-0 w-full h-1 bg-orange-500" />
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-orange-800">Loan Disbursement Pending</div>
            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-orange-500">
              <Clock className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="mt-4 font-heading text-3xl font-bold text-orange-900">
            <span data-testid="pending-disbursement-count">{stats.pending_disbursement_files || 0}</span>
            <span className="text-base font-medium text-orange-700 ml-1">Files</span>
            {(stats.overdue_disbursement_files || 0) > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200 align-middle"
                    data-testid="pending-disbursement-overdue">
                {stats.overdue_disbursement_files} Overdue · &gt;7d
              </span>
            )}
          </div>
          <div className="mt-1 text-sm font-semibold text-orange-800">
            <span data-testid="pending-disbursement-amount">{INR(stats.pending_disbursement_amount || 0)}</span>
            <span className="text-orange-700 font-normal ml-1">Total Pending</span>
          </div>
        </div>

        <div
          className="stat-card relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-teal-50 border-teal-200"
          data-testid="stat-delivery-order"
          role="button" tabIndex={0}
          onClick={() => navigate("/finance?delivery_order=Yes")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/finance?delivery_order=Yes"); }}>
          <div className="absolute top-0 left-0 w-full h-1 bg-teal-500" />
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-teal-800">Delivery Order</div>
            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-teal-500">
              <PackageCheck className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="mt-4 font-heading text-3xl font-bold text-teal-900">
            <span data-testid="delivery-order-count">{stats.delivery_order_files || 0}</span>
            <span className="text-base font-medium text-teal-700 ml-1">Files</span>
          </div>
          <div className="mt-1 text-sm font-medium text-teal-800">
            DO Issued · <span className="text-teal-700 font-normal">Delivered + Loan</span>
          </div>
        </div>

        <div
          className="stat-card relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-indigo-50 border-indigo-200"
          data-testid="stat-delivery-order-pending"
          role="button" tabIndex={0}
          onClick={() => navigate("/finance?delivery_order=No")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/finance?delivery_order=No"); }}>
          <div className="absolute top-0 left-0 w-full h-1 bg-indigo-500" />
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-indigo-800">Delivery Order Pending</div>
            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-indigo-500">
              <PackageOpen className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="mt-4 font-heading text-3xl font-bold text-indigo-900">
            <span data-testid="delivery-order-pending-count">{stats.delivery_order_pending_files || 0}</span>
            <span className="text-base font-medium text-indigo-700 ml-1">Files</span>
            {(stats.delivery_order_pending_overdue || 0) > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200 align-middle"
                    data-testid="delivery-order-pending-overdue">
                {stats.delivery_order_pending_overdue} Overdue · &gt;7d
              </span>
            )}
          </div>
          <div className="mt-1 text-sm font-medium text-indigo-800">
            DO Pending · <span className="text-indigo-700 font-normal">Delivered + Loan</span>
          </div>
        </div>
      </div>

      {/* Net Profit — Admin only */}
      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8" data-testid="net-profit-widget">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-md bg-emerald-600 flex items-center justify-center">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <div className="font-heading text-lg font-semibold text-slate-900">Net Profit</div>
              <div className="text-xs text-slate-500">Ex-Showroom − Discount − NDP · Admin-only</div>
            </div>
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs" data-testid="profit-mode-toggle">
              <button data-testid="profit-mode-range"
                onClick={() => setProfitMode("range")}
                className={`px-3 py-1.5 ${profitMode === "range" ? "bg-brand-blue text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                Date Range
              </button>
              <button data-testid="profit-mode-fy"
                onClick={() => setProfitMode("fy")}
                className={`px-3 py-1.5 ${profitMode === "fy" ? "bg-brand-blue text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                Financial Year
              </button>
            </div>
            {profitMode === "range" ? (
              <>
                <input type="date" value={profitFrom} max={profitTo || todayIso()}
                  onChange={(e) => setProfitFrom(e.target.value)}
                  data-testid="profit-date-from"
                  className="px-3 py-1.5 border border-slate-300 rounded-md text-xs" />
                <input type="date" value={profitTo} min={profitFrom} max={todayIso()}
                  onChange={(e) => setProfitTo(e.target.value)}
                  data-testid="profit-date-to"
                  className="px-3 py-1.5 border border-slate-300 rounded-md text-xs" />
              </>
            ) : (
              <select value={profitFy} onChange={(e) => setProfitFy(e.target.value)}
                data-testid="profit-fy-select"
                className="px-3 py-1.5 border border-slate-300 rounded-md text-xs bg-white">
                {(() => {
                  const d = new Date();
                  const y0 = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
                  const opts = [];
                  for (let i = -3; i <= 1; i++) {
                    const y = y0 + i;
                    opts.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
                  }
                  return opts.map(v => <option key={v} value={v}>FY {v}</option>);
                })()}
              </select>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200">
              <div className="text-xs uppercase tracking-wider text-emerald-800 font-semibold">Net Profit</div>
              <div className="font-heading text-3xl font-bold text-emerald-900 mt-1" data-testid="profit-total">
                {INR(profit?.total_net_profit || 0)}
              </div>
            </div>
            <div className="p-4 rounded-md bg-blue-50 border border-blue-200">
              <div className="text-xs uppercase tracking-wider text-blue-800 font-semibold">Delivered Vehicles</div>
              <div className="font-heading text-3xl font-bold text-blue-900 mt-1" data-testid="profit-count">
                {profit?.delivered_count || 0}
              </div>
            </div>
            <div className="p-4 rounded-md bg-slate-50 border border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Avg / Vehicle</div>
              <div className="font-heading text-3xl font-bold text-slate-900 mt-1" data-testid="profit-avg">
                {INR(profit?.avg_per_vehicle || 0)}
              </div>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button data-testid="profit-view-report"
              onClick={() => {
                const q = profitMode === "fy"
                  ? `?tab=profit&fy=${encodeURIComponent(profitFy)}`
                  : `?tab=profit&date_from=${profitFrom}&date_to=${profitTo}`;
                navigate(`/reports${q}`);
              }}
              className="text-xs text-brand-blue hover:underline">
              View full Net Profit report →
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard icon={TrendingUp} label="Total Revenue" value={INR(stats.total_revenue)} accent="bg-blue-900" testid="stat-total-revenue" />
        <StatCard icon={Landmark} label="Total Loan Amount" value={INR(stats.total_loan)} accent="bg-slate-700" testid="stat-total-loan" />
        <StatCard icon={Landmark} label="Total Down Payment" value={INR(stats.total_down_payment)} accent="bg-orange-500" testid="stat-total-downpayment" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="branch-stock-chart">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Branch-wise Stock</h3>
            <span className="text-xs uppercase tracking-wider text-slate-500">Total vehicles</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={branchData} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="branch" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {branchData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="model-stock-chart">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Model-wise Stock</h3>
            <span className="text-xs uppercase tracking-wider text-slate-500">Distribution</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={modelData} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <YAxis type="category" dataKey="model" tick={{ fontSize: 11, fill: "#64748B" }} width={110} />
              <Tooltip />
              <Bar dataKey="count" fill="#F97316" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {adv && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="today-deliveries-panel">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-md bg-orange-500 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900">Today's Deliveries</h3>
              <span className="ml-auto text-xs font-semibold text-orange-600 bg-orange-50 rounded-full px-2 py-0.5">
                {adv.today_deliveries.length}
              </span>
            </div>
            {adv.today_deliveries.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-8">No deliveries today yet</div>
            ) : (
              <div className="space-y-2">
                {adv.today_deliveries.map(v => (
                  <div key={v.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50" data-testid={`today-delivery-${v.id}`}>
                    <div className="w-8 h-8 rounded-md bg-orange-50 flex items-center justify-center">
                      <Truck className="w-4 h-4 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">{v.model}</div>
                      <div className="text-xs text-slate-500 font-mono">{v.chassis_number}</div>
                    </div>
                    <div className="text-xs text-slate-500">{v.branch}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="recent-transfers-panel">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-md bg-slate-700 flex items-center justify-center">
                <ArrowLeftRight className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900">Recent Transfers</h3>
            </div>
            {adv.recent_transfers.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-8">No transfers yet</div>
            ) : (
              <div className="space-y-2">
                {adv.recent_transfers.slice(0, 6).map(t => (
                  <div key={t.id} className="p-2 rounded-md hover:bg-slate-50" data-testid={`recent-transfer-${t.id}`}>
                    <div className="text-sm font-medium text-slate-900 truncate">{t.model}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                      {t.from_branch} <ArrowLeftRight className="w-3 h-3 text-orange-500" /> {t.to_branch}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="branch-performance-chart">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-md bg-blue-900 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900">Branch Performance</h3>
              <span className="ml-auto text-xs uppercase tracking-wider text-slate-500">This Month</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={adv.branch_performance} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="branch" tick={{ fontSize: 10, fill: "#64748B" }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="delivered" fill="#1E3A8A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Sales Target Tracker */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6" data-testid="sales-target-widget">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="w-8 h-8 rounded-md bg-orange-500 flex items-center justify-center">
            <Target className="w-4 h-4 text-white" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Sales Target Tracker</h3>
          <span className="text-xs uppercase tracking-wider text-slate-500 ml-2">Target vs Delivered</span>
          {user?.role === "admin" && (
            <button data-testid="edit-targets-button" onClick={() => setEditing(true)}
              className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-700">
              <Pencil className="w-3 h-3" /> Edit Targets
            </button>
          )}
        </div>

        {/* Date range + summary */}
        <div className="flex flex-wrap items-end gap-3 mb-5 pb-4 border-b border-slate-100">
          <label className="text-xs">
            <div className="uppercase tracking-wider text-slate-500 mb-1">From</div>
            <input type="date" value={targetFrom} max={targetTo || todayIso()}
              onChange={(e) => setTargetFrom(e.target.value)}
              data-testid="target-date-from"
              className="px-3 py-1.5 rounded-md border border-slate-300 text-sm" />
          </label>
          <label className="text-xs">
            <div className="uppercase tracking-wider text-slate-500 mb-1">To</div>
            <input type="date" value={targetTo} min={targetFrom} max={todayIso()}
              onChange={(e) => setTargetTo(e.target.value)}
              data-testid="target-date-to"
              className="px-3 py-1.5 rounded-md border border-slate-300 text-sm" />
          </label>
          <button data-testid="target-range-this-month"
            onClick={() => { setTargetFrom(monthStart()); setTargetTo(todayIso()); }}
            className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            This Month
          </button>
          <button data-testid="target-range-last-month"
            onClick={() => {
              const d = new Date();
              const last = new Date(d.getFullYear(), d.getMonth() - 1, 1);
              const end = new Date(d.getFullYear(), d.getMonth(), 0);
              setTargetFrom(last.toISOString().slice(0, 10));
              setTargetTo(end.toISOString().slice(0, 10));
            }}
            className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            Last Month
          </button>
          {targetData?.summary && (
            <div className="ml-auto flex gap-6 text-xs">
              <div>
                <div className="uppercase tracking-wider text-slate-500">Period Target</div>
                <div className="font-heading text-lg font-bold text-slate-900" data-testid="target-period-target">{targetData.summary.total_target}</div>
              </div>
              <div>
                <div className="uppercase tracking-wider text-slate-500">Delivered</div>
                <div className="font-heading text-lg font-bold text-emerald-700" data-testid="target-period-delivered">{targetData.summary.total_delivered}</div>
              </div>
              <div>
                <div className="uppercase tracking-wider text-slate-500">Remaining</div>
                <div className="font-heading text-lg font-bold text-amber-700" data-testid="target-period-remaining">{targetData.summary.remaining}</div>
              </div>
              <div>
                <div className="uppercase tracking-wider text-slate-500">Achievement</div>
                <div className="font-heading text-lg font-bold text-brand-orange" data-testid="target-period-pct">{targetData.summary.percentage}%</div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {targets.map(t => {
            const pct = Math.min(t.percentage, 100);
            const color = t.percentage >= 80 ? "bg-emerald-500" : t.percentage >= 50 ? "bg-amber-500" : "bg-red-500";
            const label = t.percentage >= 80 ? "On track" : t.percentage >= 50 ? "Behind" : "At risk";
            const labelColor = t.percentage >= 80 ? "text-emerald-700 bg-emerald-50" : t.percentage >= 50 ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";
            return (
              <div key={t.branch} className="border border-slate-200 rounded-md p-4" data-testid={`target-card-${t.branch}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-900 truncate">{t.branch}</div>
                  <span className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${labelColor}`}>{label}</span>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="font-heading text-2xl font-bold text-slate-900">{t.delivered}</span>
                  <span className="text-sm text-slate-400">/ {t.period_target ?? t.monthly_target}</span>
                  <span className="ml-auto text-xs font-semibold text-slate-600">{t.percentage}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showLedgerWidget && ledgerSummary && (
        <SubDealerLedgerWidget
          isAdmin={user?.role === "admin"}
          selected={ledgerSubDealer}
          onSelect={setLedgerSubDealer}
          summary={ledgerSummary}
        />
      )}

      {editing && (
        <EditTargetsModal targets={targets}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); loadTargets(); }} />
      )}
    </div>
  );
}

function SubDealerLedgerWidget({ isAdmin, selected, onSelect, summary }) {
  const monthLabel = (ym) => {
    // "2026-07" → "Jul 2026"
    if (!ym || ym.length < 7) return ym || "—";
    const [y, m] = ym.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  };
  const monthMax = Math.max(1, ...(summary.by_month || []).map(m => m.total));
  const modelMax = Math.max(1, ...(summary.by_model || []).map(m => m.total));

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8" data-testid="sub-dealer-ledger-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-brand-orange" />
          <div className="font-heading text-lg font-semibold text-slate-900">Sub-Dealer Profit Ledger</div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <select
              value={selected}
              onChange={(e) => onSelect(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
              data-testid="dashboard-ledger-sub-dealer"
            >
              <option value="">All Sub-Dealers</option>
              {SUB_DEALERS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MiniKpi label="Total Incentive" value={INR(summary.total)} tone="blue" testid="sd-kpi-total" />
        <MiniKpi label="This Month" value={INR(summary.this_month)} tone="orange" testid="sd-kpi-this-month" />
        <MiniKpi label="Pending" value={INR(summary.pending)} tone="amber" testid="sd-kpi-pending" />
        <MiniKpi label="Paid" value={INR(summary.paid)} tone="green" testid="sd-kpi-paid" />
      </div>

      {/* By month + By model */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Month-wise</div>
          {(summary.by_month || []).length === 0 && (
            <div className="text-sm text-slate-400 italic">No incentive entries yet.</div>
          )}
          <div className="space-y-2" data-testid="sd-by-month">
            {(summary.by_month || []).map(m => (
              <div key={m.month} className="flex items-center gap-3">
                <div className="w-24 text-xs text-slate-600">{monthLabel(m.month)}</div>
                <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden relative">
                  <div className="h-full bg-brand-orange" style={{ width: `${(m.total / monthMax) * 100}%` }} />
                  <div className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-semibold text-slate-800">
                    {INR(m.total)} · <span className="text-slate-500 ml-1">{m.count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Model-wise</div>
          {(summary.by_model || []).length === 0 && (
            <div className="text-sm text-slate-400 italic">No incentive entries yet.</div>
          )}
          <div className="space-y-2" data-testid="sd-by-model">
            {(summary.by_model || []).map(m => (
              <div key={m.model} className="flex items-center gap-3">
                <div className="w-40 text-xs text-slate-700 truncate">{m.model}</div>
                <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden relative">
                  <div className="h-full bg-blue-900" style={{ width: `${(m.total / modelMax) * 100}%` }} />
                  <div className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-semibold text-slate-800">
                    {INR(m.total)} · <span className="text-slate-500 ml-1">{m.count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const KPI_TONES = {
  blue: "bg-blue-50 text-blue-800 border-blue-200",
  orange: "bg-orange-50 text-brand-orange border-orange-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  green: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
function MiniKpi({ label, value, tone, testid }) {
  return (
    <div className={`p-3 rounded-md border ${KPI_TONES[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
      <div className="text-lg font-bold mt-1">{value}</div>
    </div>
  );
}

function EditTargetsModal({ targets, onClose, onSaved }) {
  const [vals, setVals] = useState(Object.fromEntries(targets.map(t => [t.branch, t.monthly_target])));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      for (const [branch, target] of Object.entries(vals)) {
        await api.put(`/branch-targets/${encodeURIComponent(branch)}`, { monthly_target: Number(target || 0) });
      }
      toast.success("Branch targets updated");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="edit-targets-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="font-heading text-xl font-semibold mb-4">Edit Monthly Sales Targets</div>
        <div className="space-y-3">
          {Object.entries(vals).map(([branch, val]) => (
            <label key={branch} className="flex items-center gap-3">
              <div className="flex-1 text-sm font-medium text-slate-700">{branch}</div>
              <input type="number" min="0" value={val}
                onChange={(e) => setVals({ ...vals, [branch]: e.target.value })}
                className="w-24 px-3 py-2 rounded-md border border-slate-300 text-sm text-right"
                data-testid={`target-input-${branch}`} />
              <span className="text-xs text-slate-400 w-14">/ month</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-slate-300"
            data-testid="target-cancel">Cancel</button>
          <button type="submit" disabled={saving} data-testid="target-save"
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60">
            {saving ? "Saving…" : "Save Targets"}
          </button>
        </div>
      </form>
    </div>
  );
}
